import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { CardModalProps, CardType, ApiCardField, CardUpdatePayload, CardCreatePayload, CardViewResponse, CardSection, CardViewField } from '../../types/index';
import {
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  FieldGroup,
  FieldLabel,
  FieldTextarea,
  FieldSelect,
  FieldInput,
  ModalActions,
  CancelButton,
  SubmitButton,
  ScrollContainer,
  ModalWrapper,
  MainContent,
  Sidebar,
  SidebarSection,
  SidebarTitle,
  ClickableField,
  ClickableFieldText,
  ClickableFieldHint,
  ModalFooter,
  NotesTextarea,
  BooleanOptionsContainer,
  BooleanOptionLabel
} from '../../styles/index';
import refreshToken from '../../../../utils/refreshToken';
import { useToast } from '../../../../hooks/toast';
import { useAuth } from '../../../../hooks/auth';
import api from '../../../../services/api';
import { parseKanbanFieldOptions } from '../../utils/parseFieldOptions';
import { CardFieldSelectAddOption } from '../CardFieldSelectAddOption';
import MaskedInput from 'react-text-mask';
import CurrencyInput from 'react-currency-input-field';
import { FieldErrorMessage, FieldSectionContainer, SubSectionTitle, ModalHeaderRow, ModalHeaderTitle, FavoriteButton, DatesContainer, DateItem, DateValue, errorInputStyle } from './styles';

function displaySectionHeading(title: string | null | undefined): string {
  const raw = title ?? '';
  const folded = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return folded === 'sem secao' ? 'Outros' : raw;
}

interface ModalCardField {
  id: number;
  label: string;
  field_type: string;
  value_type?: string;
  placeholder: string;
  options?: Array<string | number | { label?: string; name?: string; value?: string | number; id?: number; first_name?: string }> | any[];
  value: string;
  isEditing: boolean;
  is_required: boolean;
  originalValue: string;
  sectionId: number | null;
  sectionTitle: string;
  sectionPosition: number;
  sectionCollapsed: boolean;
  fieldPosition: number;
  orderIndex: number;
  isLocked?: boolean;
}

interface ColumnFieldsResponse {
  column_id: number;
  required_fields: ApiCardField[];
}

interface ColumnFieldsApiResponse {
  success: boolean;
  data: ColumnFieldsResponse;
}

interface EditedCardType extends Partial<CardType> {
  isEditingDescription?: boolean;
  isEditingPriority?: boolean;
}

interface SectionGroup {
  sectionId: number | null;
  sectionTitle: string;
  fields: ModalCardField[];
}

interface FieldGroups {
  requiredAndEmpty: SectionGroup[];
  filled: SectionGroup[];
  optionalAndEmpty: SectionGroup[];
}

const FieldGroupRenderer = React.memo(({ field, isNewCard, onFieldChange, onToggleEdit, hasError, errorMessage, onCreateSelectOption }: {
  field: ModalCardField;
  isNewCard: boolean;
  onFieldChange: (fieldId: number, value: string) => void;
  onToggleEdit: (fieldId: number) => void;
  hasError?: boolean;
  errorMessage?: string;
  onCreateSelectOption?: (cardFieldId: number, label: string) => Promise<void>;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (field.isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [field.isEditing]);

  const handleLocalChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    onFieldChange(field.id, e.target.value);
  }, [field.id, onFieldChange]);

  const isLocked = Boolean(field.isLocked);

  const handleLocalToggleEdit = useCallback(() => {
    if (isLocked) return;
    if (!isNewCard) onToggleEdit(field.id);
  }, [field.id, isNewCard, onToggleEdit, isLocked]);

  const handleBlur = useCallback(() => {
    if (isLocked) return;
    if (!isNewCard && field.isEditing) onToggleEdit(field.id);
  }, [field.id, isNewCard, field.isEditing, onToggleEdit, isLocked]);

  const rawOptions = parseKanbanFieldOptions(field.options);
  const normalizedOptions = useMemo(() => {
    return rawOptions.map((option: any) => {
      if (typeof option === 'string' || typeof option === 'number') {
        const optionValue = String(option);
        return { value: optionValue, label: optionValue };
      }

      const optionValue = String(option?.id ?? option?.value ?? '');
      const optionLabel = String(option?.first_name ?? option?.label ?? option?.name ?? optionValue);

      return {
        value: optionValue,
        label: optionLabel
      };
    }).filter(option => option.value !== '');
  }, [rawOptions]);

  const getDisplayValue = useCallback(() => {
    if (!field.originalValue) return '';

    const normalizedLabel = (field.label || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const isConsultorField = normalizedLabel.includes('consultor');

    if (isConsultorField && normalizedOptions.length > 0) {
      const selectedOption = normalizedOptions.find(opt => opt.value === field.originalValue);
      return selectedOption ? selectedOption.label : field.originalValue;
    }

    if (field.value_type === 'boolean' || field.field_type === 'boolean' || field.field_type === 'booleano') {
      return field.originalValue === '1' ? 'Sim' : field.originalValue === '0' ? 'Não' : field.originalValue;
    }

    return field.originalValue;
  }, [field.originalValue, field.label, field.value_type, field.field_type, normalizedOptions]);

  const isViewingMode = isLocked || (!isNewCard && !!field.originalValue && !field.isEditing);

  const unmaskDigits = useCallback((v: string) => (v || '').replace(/\D/g, ''), []);

  const toDateInputValue = useCallback((rawValue: string): string => {
    const raw = String(rawValue || '').trim();
    if (!raw) return '';

    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;

    if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(raw)) {
      return raw.slice(0, 10);
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split('/');
      return `${year}-${month}-${day}`;
    }

    return '';
  }, []);

  const toDateTimeLocalValue = useCallback((rawValue: string): string => {
    const raw = String(rawValue || '').trim();
    if (!raw) return '';

    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw)) return raw;

    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(raw)) {
      const [date, time] = raw.split(' ');
      return `${date}T${time.slice(0, 5)}`;
    }

    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(raw)) {
      return raw.slice(0, 16);
    }

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      const hours = String(parsed.getHours()).padStart(2, '0');
      const minutes = String(parsed.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    return '';
  }, []);

  const fromDateTimeLocalToApi = useCallback((datetimeLocal: string): string => {
    const value = String(datetimeLocal || '').trim();
    if (!value) return '';
    const [date, time] = value.split('T');
    if (!date || !time) return value;
    return `${date} ${time}:00`;
  }, []);

  const detectSpecial = useCallback((): string | null => {
    const label = (field.label || '').toLowerCase();
    const normalizedLabel = label.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const ft = ((field.field_type || '') + '').toLowerCase();
    const vt = ((field.value_type || '') + '').toLowerCase();
    const normalizedType = vt || ft;

    const digitsOnly = String(field.value || '').replace(/\D/g, '');

    const hasBinaryOptions = normalizedOptions.length === 2 && (
      (normalizedOptions.some(o => o.value === '0') && normalizedOptions.some(o => o.value === '1')) ||
      (normalizedOptions.some(o => o.label.toLowerCase().includes('sim')) &&
        (normalizedOptions.some(o => o.label.toLowerCase().includes('nao')) || normalizedOptions.some(o => o.label.toLowerCase().includes('não')))) ||
      (normalizedOptions.some(o => o.value.toLowerCase() === 'true') && normalizedOptions.some(o => o.value.toLowerCase() === 'false'))
    );

    const labelSuggestsBoolean =
      normalizedLabel.includes('administracao') ||
      normalizedLabel.includes('7cantos') ||
      normalizedLabel.includes('ativo') ||
      normalizedLabel.includes('status');

    const labelSuggestsDateTime =
      normalizedLabel.includes('agendamento') ||
      normalizedLabel.includes('horario') ||
      normalizedLabel.includes('hora');

    if (digitsOnly.length > 0) {
      if (digitsOnly.length === 11 && (normalizedLabel.includes('cpf') || normalizedType.includes('cpf'))) return 'cpf';
      if (digitsOnly.length === 14 && (normalizedLabel.includes('cnpj') || normalizedType.includes('cnpj'))) return 'cnpj';
      if (digitsOnly.length === 11 && (normalizedLabel.includes('cnh') || normalizedType.includes('cnh'))) return 'cnh';
      if (digitsOnly.length >= 9 && digitsOnly.length <= 10 && (normalizedLabel.includes('rg') || normalizedType.includes('rg'))) return 'rg';
    }

    if (vt === 'boolean' || ft === 'boolean' || ft === 'booleano' || ft === 'checkbox') return 'boolean';
    if ((ft === 'radio' || ft === 'select') && (hasBinaryOptions || labelSuggestsBoolean)) return 'boolean';
    if (hasBinaryOptions) return 'boolean';
    if (labelSuggestsBoolean) return 'boolean';
    if (ft === 'phone' || ft === 'telefone' || normalizedType === 'phone' || normalizedType === 'telefone' || label.includes('telefone') || label.includes('celular')) return 'phone';
    if (normalizedLabel.includes('cpf') || normalizedType.includes('cpf')) return 'cpf';
    if (normalizedLabel.includes('cnpj') || normalizedType.includes('cnpj')) return 'cnpj';
    if (normalizedLabel.includes('cnh') || normalizedType.includes('cnh')) return 'cnh';
    if (normalizedLabel.includes('rg') || normalizedType.includes('rg')) return 'rg';
    if (normalizedLabel.includes('cep') || ft === 'cep') return 'cep';
    if (vt.includes('datetime') || ft.includes('datetime') || ft.includes('timestamp')) return 'datetime';
    if ((ft.includes('date') || ft.includes('data') || normalizedLabel.includes('data')) && labelSuggestsDateTime) return 'datetime';
    if (labelSuggestsDateTime) return 'datetime';
    if (ft.includes('date') || ft.includes('data') || normalizedLabel.includes('data')) return 'date';
    if (normalizedLabel.includes('valor') || ft.includes('currency') || ft.includes('moeda') || normalizedLabel.includes('preco')) return 'currency';
    if (normalizedLabel.includes('%') || normalizedLabel.includes('percent') || ft.includes('percent')) return 'percent';
    return null;
  }, [field, normalizedOptions]);

  const special = detectSpecial();
  const maskedDisplayValue = useMemo(() => {
    if (special !== 'date') return field.value || '';

    const rawValue = field.value || '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
      const [year, month, day] = rawValue.split('-');
      return `${day}/${month}/${year}`;
    }

    return rawValue;
  }, [field.value, special]);

  const ft = (field.field_type || '').toString().toLowerCase();
  const vt = (field.value_type || '').toString().toLowerCase();
  const normalizedLabel = (field.label || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const isConsultorField = normalizedLabel.includes('consultor');

  const isBooleanField = vt === 'boolean' || ft === 'boolean' || ft === 'booleano' || special === 'boolean';

  const selectFallbackPlaceholder =
    field.placeholder || 'Digite o valor (opções não carregadas)…';

  return (
    <FieldGroup data-field-id={field.id}>
      <FieldLabel>{field.label}{field.is_required && <span> *</span>}</FieldLabel>
      {isViewingMode ? (
        <ClickableField onClick={handleLocalToggleEdit}>
          <ClickableFieldText>{getDisplayValue()}</ClickableFieldText>
          <ClickableFieldHint>{isLocked ? 'Automático' : 'Editar'}</ClickableFieldHint>
        </ClickableField>
      ) : (
        (() => {
          if (isConsultorField) {
            if (normalizedOptions.length === 0) {
              return (
                <FieldInput
                  value={field.value}
                  onChange={handleLocalChange}
                  onBlur={handleBlur}
                  placeholder={selectFallbackPlaceholder}
                  autoFocus={field.isEditing}
                  ref={inputRef}
                  style={hasError ? errorInputStyle : undefined}
                  title="Lista de consultores indisponível — use texto livre."
                />
              );
            }
            return (
              <FieldSelect value={field.value} onChange={handleLocalChange} onBlur={handleBlur} autoFocus={field.isEditing} ref={inputRef as any} style={hasError ? errorInputStyle : undefined}>
                <option value="">{field.placeholder || 'Selecione um consultor...'}</option>
                {normalizedOptions.map(option => (
                  <option key={`${field.id}-${option.value}`} value={option.value}>{option.label}</option>
                ))}
              </FieldSelect>
            );
          }

          if (ft === 'textarea') {
            return (
              <FieldTextarea
                value={field.value}
                onChange={handleLocalChange}
                onBlur={handleBlur}
                placeholder={field.placeholder || 'Digite aqui...'}
                autoFocus={field.isEditing}
                ref={inputRef as any}
                style={hasError ? errorInputStyle : undefined}
              />
            );
          }

          if (isBooleanField) {
            return (
              <BooleanOptionsContainer style={hasError ? errorInputStyle : undefined}>
                <BooleanOptionLabel>
                  <input
                    type="radio"
                    name={`bool_${field.id}`}
                    value="1"
                    checked={String(field.value) === '1'}
                    onChange={() => onFieldChange(field.id, '1')}
                  />
                  Sim
                </BooleanOptionLabel>
                <BooleanOptionLabel>
                  <input
                    type="radio"
                    name={`bool_${field.id}`}
                    value="0"
                    checked={String(field.value) === '0'}
                    onChange={() => onFieldChange(field.id, '0')}
                  />
                  Não
                </BooleanOptionLabel>
              </BooleanOptionsContainer>
            );
          }

          if (special === 'currency') {
            return (
              <CurrencyInput
                customInput={FieldInput as any}
                intlConfig={{ locale: 'pt-BR', currency: 'BRL' }}
                value={field.value || ''}
                decimalsLimit={2}
                decimalSeparator=","
                groupSeparator="."
                prefix="R$ "
                onValueChange={(value) => onFieldChange(field.id, value || '')}
                onBlur={handleBlur}
                placeholder={field.placeholder || '0,00'}
                autoFocus={field.isEditing}
                style={hasError ? errorInputStyle : undefined}
              />
            );
          }

          if (special === 'percent') {
            return (
              <FieldInput
                value={field.value}
                onChange={(e) => onFieldChange(field.id, e.target.value.replace(/[^0-9,.-]/g, ''))}
                onBlur={handleBlur}
                placeholder={field.placeholder || '0,00%'}
                autoFocus={field.isEditing}
                ref={inputRef}
                style={hasError ? errorInputStyle : undefined}
              />
            );
          }

          if (special === 'datetime') {
            return (
              <FieldInput
                type="datetime-local"
                value={toDateTimeLocalValue(field.value)}
                onChange={(e) => onFieldChange(field.id, fromDateTimeLocalToApi(e.target.value))}
                onBlur={handleBlur}
                placeholder={field.placeholder || ''}
                autoFocus={field.isEditing}
                ref={inputRef}
                style={hasError ? errorInputStyle : undefined}
              />
            );
          }

          if (special === 'date') {
            return (
              <FieldInput
                type="date"
                value={toDateInputValue(field.value)}
                onChange={(e) => onFieldChange(field.id, e.target.value)}
                onBlur={handleBlur}
                placeholder={field.placeholder || ''}
                autoFocus={field.isEditing}
                ref={inputRef}
                style={hasError ? errorInputStyle : undefined}
              />
            );
          }

          if (special === 'phone' || special === 'cpf' || special === 'cnpj' || special === 'rg' || special === 'cnh' || special === 'cep') {
            let mask: any = null;
            if (special === 'phone') mask = ['(', /[0-9]/, /[0-9]/, ')', ' ', /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, '-', /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];
            else if (special === 'cpf') mask = [/[0-9]/, /[0-9]/, /[0-9]/, '.', /[0-9]/, /[0-9]/, /[0-9]/, '.', /[0-9]/, /[0-9]/, /[0-9]/, '-', /[0-9]/, /[0-9]/];
            else if (special === 'cnpj') mask = [/[0-9]/, /[0-9]/, '.', /[0-9]/, /[0-9]/, /[0-9]/, '.', /[0-9]/, /[0-9]/, /[0-9]/, '/', /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, '-', /[0-9]/, /[0-9]/];
            else if (special === 'rg') mask = [/[0-9]/, /[0-9]/, '.', /[0-9]/, /[0-9]/, /[0-9]/, '.', /[0-9]/, /[0-9]/, /[0-9]/, '-', /[0-9]/];
            else if (special === 'cnh') mask = [/[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/];
            else if (special === 'cep') mask = [/[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/, '-', /[0-9]/, /[0-9]/, /[0-9]/];

            return (
              <MaskedInput
                mask={mask}
                guide={false}
                value={maskedDisplayValue}
                onChange={(e: any) => {
                  const v = e.target.value || '';
                  onFieldChange(field.id, unmaskDigits(v));
                }}
                onBlur={handleBlur}
                placeholder={field.placeholder || ''}
                render={(ref, props) => (
                  <FieldInput
                    ref={ref as any}
                    {...props as any}
                    autoFocus={field.isEditing}
                    style={hasError ? errorInputStyle : undefined}
                  />
                )}
              />
            );
          }

          if (ft === 'select' || ft === 'multiselect' || ft === 'radio') {
            if (normalizedOptions.length === 0) {
              return (
                <>
                  <FieldInput
                    value={field.value}
                    onChange={handleLocalChange}
                    onBlur={handleBlur}
                    placeholder={selectFallbackPlaceholder}
                    autoFocus={field.isEditing}
                    ref={inputRef}
                    style={hasError ? errorInputStyle : undefined}
                    title="Lista de opções indisponível — use texto livre ou adicione uma opção abaixo."
                  />
                  {onCreateSelectOption ? (
                    <CardFieldSelectAddOption
                      cardFieldId={field.id}
                      onCreate={onCreateSelectOption}
                    />
                  ) : null}
                </>
              );
            }
            return (
              <>
                <FieldSelect value={field.value} onChange={handleLocalChange} onBlur={handleBlur} autoFocus={field.isEditing} ref={inputRef as any} style={hasError ? errorInputStyle : undefined}>
                  <option value="">{field.placeholder || 'Selecione...'}</option>
                  {normalizedOptions.map(option => (
                    <option key={`${field.id}-${option.value}`} value={option.value}>{option.label}</option>
                  ))}
                </FieldSelect>
                {onCreateSelectOption ? (
                  <CardFieldSelectAddOption
                    cardFieldId={field.id}
                    onCreate={onCreateSelectOption}
                  />
                ) : null}
              </>
            );
          }

          return (
            <FieldInput
              value={field.value}
              onChange={handleLocalChange}
              onBlur={handleBlur}
              placeholder={field.placeholder || 'Digite aqui...'}
              autoFocus={field.isEditing}
              ref={inputRef}
              style={hasError ? errorInputStyle : undefined}
            />
          );
        })()
      )}
      {hasError && (
        <FieldErrorMessage>
          {errorMessage || 'Campo obrigatório'}
        </FieldErrorMessage>
      )}
    </FieldGroup>
  );
});

function CardModal({ isOpen, onClose, card, onSave, onCreate, boardId }: CardModalProps) {
  const isNewCard = !card?.id;
  const [editedCard, setEditedCard] = useState<EditedCardType>(card);
  const [formFields, setFormFields] = useState<ModalCardField[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [annotation, setAnnotation] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const { access_token, dateToExpires, user } = useAuth();
  const userId = user?.id;
  const { addToast } = useToast();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const modalContentRef = useRef<HTMLDivElement | null>(null);
  const initializedRef = useRef(false);

  const formatDateAndTime = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).replace(',', ' ');
    } catch {
      return dateString;
    }
  };

  const normalizeText = useCallback((value: string): string => (
    String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim()
  ), []);

  const isPhaseFieldLabel = useCallback((label: string): boolean => {
    const normalized = normalizeText(label);
    return normalized.includes('fase') || normalized.includes('etapa');
  }, [normalizeText]);

  const currentColumnName = useMemo(() => {
    const editedName = (editedCard as any)?.column_name;
    const cardName = (card as any)?.column_name;
    return String(editedName || cardName || '').trim();
  }, [editedCard, card]);

  const handleFieldChange = useCallback((fieldId: number, value: string) => {
    setFormFields(prevFields =>
      prevFields.map(f => {
        if (f.id !== fieldId) return f;
        if (f.isLocked) return f;
        return { ...f, value };
      })
    );

    setFieldErrors(prev => {
      if (!prev[fieldId]) return prev;
      if (String(value ?? '').trim()) {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      }
      return prev;
    });

    setHasChanges(true);
  }, []);

  const toggleFieldEdit = useCallback((fieldId: number) => {
    setFormFields(prevFields =>
      prevFields.map(f => (f.id === fieldId ? { ...f, isEditing: !f.isEditing } : f))
    );
  }, []);

  const handleCreateCardFieldOption = useCallback(
    async (cardFieldId: number, label: string) => {
      const text = label.trim();
      if (!text) {
        addToast({ type: "info", title: "Informe o nome da nova opção." });
        return;
      }
      const fieldRow = formFields.find((f) => f.id === cardFieldId);
      const position = fieldRow
        ? parseKanbanFieldOptions(fieldRow.options).length
        : 0;

      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token || access_token}`;
        await api.post(`/api/adm/card-fields/${cardFieldId}/options`, {
          value: text,
          label: text,
          position,
        });
        setFormFields((prev) =>
          prev.map((f) => {
            if (f.id !== cardFieldId) return f;
            const next = [...(Array.isArray(f.options) ? f.options : [])];
            next.push({ value: text, label: text });
            return { ...f, options: next };
          })
        );
        handleFieldChange(cardFieldId, text);
        addToast({ type: "success", title: "Opção adicionada." });
      } catch (e: any) {
        addToast({
          type: "error",
          title:
            e?.response?.data?.message ||
            "Não foi possível adicionar a opção.",
        });
      }
    },
    [
      formFields,
      access_token,
      dateToExpires,
      addToast,
      handleFieldChange,
    ]
  );

  const getErrorStatus = useCallback((error: any): number | null => {
    const status = error?.response?.status;
    return typeof status === 'number' ? status : null;
  }, []);

  const parseBooleanLike = useCallback((value: unknown): boolean | null => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === '1' || normalized === 'true' || normalized === 'sim') return true;
      if (normalized === '0' || normalized === 'false' || normalized === 'nao' || normalized === 'não') return false;
    }
    return null;
  }, []);

  const getServerFavoriteState = useCallback((serverCardLike: any): boolean => {
    const myFavorites = parseBooleanLike(serverCardLike?.myFavorites);
    if (myFavorites !== null) return myFavorites;

    const isFavorite = parseBooleanLike(serverCardLike?.is_favorite);
    if (isFavorite !== null) return isFavorite;

    if (Array.isArray(serverCardLike?.favorites) && userId) {
      return serverCardLike.favorites.some((fav: any) => Number(fav?.id) === Number(userId));
    }

    return false;
  }, [parseBooleanLike, userId]);

  const orderFieldsByBusinessRule = useCallback((fields: ModalCardField[]): ModalCardField[] => {
    const requiredAndEmpty: ModalCardField[] = [];
    const filled: ModalCardField[] = [];
    const optionalAndEmpty: ModalCardField[] = [];

    fields.forEach(field => {
      const normalizedValue = String(field.value ?? '').trim();
      if (field.is_required && !normalizedValue) {
        requiredAndEmpty.push(field);
      } else if (normalizedValue) {
        filled.push(field);
      } else {
        optionalAndEmpty.push(field);
      }
    });

    return [...requiredAndEmpty, ...filled, ...optionalAndEmpty];
  }, []);

  const mapTemplateFieldsToModalFields = useCallback((fields: ApiCardField[]): ModalCardField[] => {
    const flattened = fields.map((field, index) => {
      const baseValue = String((field as any)?.value ?? '');
      const phaseField = isPhaseFieldLabel(field.label || '');
      const value = phaseField && currentColumnName ? currentColumnName : baseValue;
      const hasExistingValue = value.trim().length > 0;

      return {
        id: field.id,
        label: field.label,
        field_type: field.field_type,
        value_type: (field as any).value_type,
        placeholder: field.placeholder,
        options: field.options,
        value,
        originalValue: value,
        isEditing: isNewCard ? true : !hasExistingValue,
        is_required: isNewCard ? true : Boolean(field.is_required),
        sectionId: null,
        sectionTitle: '',
        sectionPosition: 0,
        sectionCollapsed: false,
        fieldPosition: index,
        orderIndex: index,
        isLocked: phaseField,
      } as ModalCardField;
    });

    return orderFieldsByBusinessRule(flattened);
  }, [isNewCard, orderFieldsByBusinessRule, isPhaseFieldLabel, currentColumnName]);

  const mapSectionsToModalFields = useCallback((sections: CardSection[]): ModalCardField[] => {
    let orderIndex = 0;

    const flattened = [...(sections || [])]
      .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0))
      .flatMap(section => {
        const sectionTitle = section?.title ?? '';
        return [...(section?.fields || [])]
          .filter(field => !field.hidden)
          .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0))
          .map((field: CardViewField) => {
            const phaseField = isPhaseFieldLabel(field.label || '');
            const baseValue = String(field.value ?? '');
            const value = phaseField && currentColumnName ? currentColumnName : baseValue;
            const hasExistingValue = value.trim().length > 0;

            const mappedField: ModalCardField = {
              id: field.id,
              label: field.label,
              field_type: field.field_type,
              value_type: (field as any).value_type,
              placeholder: field.placeholder,
              options: field.options,
              value,
              originalValue: value,
              isEditing: isNewCard ? true : !hasExistingValue,
              is_required: Boolean(field.required),
              sectionId: section?.id ?? null,
              sectionTitle,
              sectionPosition: Number(section?.position ?? 0),
              sectionCollapsed: Boolean(section?.collapsed),
              fieldPosition: Number(field.position ?? 0),
              orderIndex: orderIndex++,
              isLocked: phaseField,
            };

            return mappedField;
          });
      });

    return orderFieldsByBusinessRule(flattened);
  }, [isNewCard, orderFieldsByBusinessRule, isPhaseFieldLabel, currentColumnName]);

  const extractColumnTemplateFields = useCallback((raw: any): ApiCardField[] => {
    const root = raw?.data ?? raw;

    const requiredFields = Array.isArray(root?.required_fields)
      ? root.required_fields
      : Array.isArray(raw?.required_fields)
        ? raw.required_fields
        : [];

    if (requiredFields.length > 0) {
      return requiredFields.map((fieldLike: any) => ({
        ...fieldLike,
        is_required: Boolean(fieldLike?.is_required ?? fieldLike?.required),
        columns: Array.isArray(fieldLike?.columns) ? fieldLike.columns : [],
        required_column: fieldLike?.required_column ?? null,
      })) as ApiCardField[];
    }

    const fieldsFromRoot = Array.isArray(root?.fields)
      ? root.fields
      : Array.isArray(raw?.fields)
        ? raw.fields
        : [];

    if (fieldsFromRoot.length > 0) {
      return fieldsFromRoot.map((fieldLike: any) => ({
        ...fieldLike,
        is_required: Boolean(fieldLike?.is_required ?? fieldLike?.required),
        columns: Array.isArray(fieldLike?.columns) ? fieldLike.columns : [],
        required_column: fieldLike?.required_column ?? null,
      })) as ApiCardField[];
    }

    const sections = Array.isArray(root?.sections)
      ? root.sections
      : Array.isArray(raw?.sections)
        ? raw.sections
        : [];

    if (sections.length > 0) {
      return sections.flatMap((section: any) => {
        const sectionFields = Array.isArray(section?.fields) ? section.fields : [];
        return sectionFields.map((fieldLike: any) => ({
          id: Number(fieldLike?.id),
          label: String(fieldLike?.label ?? ''),
          field_type: String(fieldLike?.field_type ?? 'text'),
          value_type: fieldLike?.value_type,
          placeholder: String(fieldLike?.placeholder ?? ''),
          options: fieldLike?.options,
          is_required: Boolean(fieldLike?.is_required ?? fieldLike?.required),
          columns: [],
          required_column: null,
        } as ApiCardField));
      });
    }

    return [];
  }, []);

  const resetScrollToTop = useCallback(() => {
    requestAnimationFrame(() => {
      try {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = 0;
        }
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = 0;
        }
      } catch (e) {
      }
    });
  }, []);

  useEffect(() => {
    let isMounted = true;
    initializedRef.current = false;

    if (isOpen) {
      const fetchData = async () => {
        try {
          const token = await refreshToken(dateToExpires, access_token);
          api.defaults.headers.authorization = `Bearer ${token || access_token}`;

          setEditedCard(card);

          let processedFields: ModalCardField[] = [];
          let cardToEdit: Partial<CardType> = card;
          let currentAnnotation = (card as any)?.annotation || '';

          if (isNewCard) {
            const currentColumnId = card?.column_id;
            if (currentColumnId === undefined || currentColumnId === null) {
              addToast({ type: 'error', title: 'Erro', description: 'Coluna não identificada' });
              return;
            }

            const response = await api.get(`/api/adm/columns/${currentColumnId}/required-fields`);
            const apiData: ColumnFieldsApiResponse = response.data;
            const requiredFieldsFromApi = apiData?.data?.required_fields || [];
            const templateFields = extractColumnTemplateFields({ required_fields: requiredFieldsFromApi });

            processedFields = mapTemplateFieldsToModalFields(templateFields);
          } else if (card?.id) {
            const cardResp = await api.get(`/api/adm/cards/${card.id}/view`);
            const data: CardViewResponse = cardResp.data;
            const cardData = data?.card;
            const sections = data?.sections || [];

            if (cardData) {
              cardToEdit = {
                ...(card as any),
                ...cardData,
                myFavorites: getServerFavoriteState(card),
              };
              currentAnnotation = cardData.annotation || '';
            }

            processedFields = mapSectionsToModalFields(sections);
          }

          if (isMounted) {
            setEditedCard(cardToEdit as EditedCardType);
            setFormFields(processedFields);
            setAnnotation(currentAnnotation);
            setFieldErrors({});
            setHasChanges(false);
            initializedRef.current = true;
            resetScrollToTop();
          }

        } catch (error) {
          if (isMounted) {
            addToast({ type: 'error', title: 'Erro ao carregar dados' });
          }
        }
      };

      fetchData();
    }

    return () => {
      isMounted = false;
    };
  }, [
    isOpen,
    card,
    isNewCard,
    access_token,
    dateToExpires,
    addToast,
    boardId,
    mapTemplateFieldsToModalFields,
    mapSectionsToModalFields,
    extractColumnTemplateFields,
    getServerFavoriteState,
    resetScrollToTop,
  ]);

  const fieldGroups = useMemo<FieldGroups>(() => {
    const requiredAndEmpty: ModalCardField[] = [];
    const filled: ModalCardField[] = [];
    const optionalAndEmpty: ModalCardField[] = [];

    formFields.forEach(field => {
      const persistedValue = String(field.originalValue ?? '').trim();
      if (field.is_required && !persistedValue) {
        requiredAndEmpty.push(field);
      } else if (persistedValue) {
        filled.push(field);
      } else {
        optionalAndEmpty.push(field);
      }
    });

    const toSectionGroups = (fields: ModalCardField[]): SectionGroup[] => {
      const groupsMap = new Map<string, SectionGroup>();

      fields.forEach(field => {
        const key = `${field.sectionId ?? 'no-section'}::${field.sectionTitle ?? ''}`;

        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            sectionId: field.sectionId,
            sectionTitle: field.sectionTitle ?? '',
            fields: [],
          });
        }

        groupsMap.get(key)!.fields.push(field);
      });

      return Array.from(groupsMap.values());
    };

    return {
      requiredAndEmpty: toSectionGroups(requiredAndEmpty),
      filled: toSectionGroups(filled),
      optionalAndEmpty: toSectionGroups(optionalAndEmpty),
    };
  }, [formFields]);

  const effectiveHasChanges = useMemo(() => {
    const fieldsDirty = formFields.some(f => (f.value || '') !== (f.originalValue || ''));
    const descriptionDirty = (editedCard?.description || '') !== (card?.description || '');
    const priorityDirty = (editedCard?.priority || '') !== (card?.priority || '');
    const favoritesDirty = (editedCard as any)?.myFavorites !== (card as any)?.myFavorites;
    const annotationDirty = (annotation || '') !== ((card as any)?.annotation || '');
    const result = hasChanges || fieldsDirty || descriptionDirty || priorityDirty || favoritesDirty || annotationDirty;
    return result;
  }, [formFields, editedCard, card, hasChanges, annotation]);

  if (!isOpen) return null;

  const handleDescriptionChange = (value: string) => {
    setEditedCard(prev => ({ ...prev, description: value }));
    setHasChanges(true);
  };

  const handlePriorityChange = (value: string) => {
    setEditedCard(prev => ({ ...prev, priority: value as CardType['priority'] }));
    setHasChanges(true);
  };

  const handleFavoriteToggle = () => {
    setEditedCard(prev => ({ ...prev, myFavorites: !prev.myFavorites }));
    setHasChanges(true);
  };

  const handleAnnotationChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAnnotation(e.target.value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (isNewCard && !onCreate) return;
    if (!isNewCard && !onSave) return;

    const emptyRequiredFields = formFields.filter(field => field.is_required && !String(field.value ?? '').trim());
    if (emptyRequiredFields.length > 0) {
      const nextErrors = emptyRequiredFields.reduce<Record<number, string>>((acc, field) => {
        acc[field.id] = 'Campo obrigatório não preenchido';
        return acc;
      }, {});
      setFieldErrors(nextErrors);

      const firstErrorField = emptyRequiredFields[0];
      if (firstErrorField) {
        requestAnimationFrame(() => {
          const root = modalContentRef.current || document;
          const target = root.querySelector(`[data-field-id="${firstErrorField.id}"]`) as HTMLElement | null;
          target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }

      const fieldNames = emptyRequiredFields.map(f => f.label).join(', ');
      addToast({
        type: 'error',
        title: 'Campos obrigatórios não preenchidos',
        description: `Por favor, preencha os seguintes campos: ${fieldNames}`
      });
      return;
    }

    setFieldErrors({});

    setIsSaving(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token || access_token}`;

      const normalizeValue = (value: unknown): string => {
        if (value === null || value === undefined) return '';
        return String(value).trim();
      };

      if (isNewCard) {
        if (!editedCard.column_id) return;

        const cardFieldsPayloadCreate = formFields
          .filter(field => field.value !== '')
          .map(({ id, value }) => ({
            card_field_id: id,
            value: normalizeValue(value)
          }));

        const newCardPayload = {
          description: editedCard.description || '',
          position: 1,
          column_id: editedCard.column_id,
          priority: editedCard.priority || 'important',
          annotation: annotation || '',
          card_fields: cardFieldsPayloadCreate,
        };

        const newlyCreatedCard = await onCreate!(newCardPayload as CardCreatePayload);
        onSave?.(newlyCreatedCard);
        addToast({ type: 'success', title: 'Card criado com sucesso!' });
        onClose();

      } else {
        const currentCard = card as CardType;
        const cardId = Number(editedCard.id);

        const cardFieldsPayloadUpdate = formFields.map(({ id, value }) => ({
          id: id,
          value: normalizeValue(value)
        }));

        const basePayload = {
          description: editedCard.description || '',
          annotation: annotation || '',
          priority: editedCard.priority || 'important',
          card_fields: cardFieldsPayloadUpdate,
        };

        try {
          const updateResp = await api.put(`/api/adm/cards/${cardId}`, basePayload);

          let finalMyFavorites = !!editedCard.myFavorites;
          const currentFavoriteState = !!currentCard.myFavorites;

          if (finalMyFavorites !== currentFavoriteState && userId) {
            const url = `/api/adm/cards/${cardId}/favorite?userId=${userId}`;
            try {
              if (finalMyFavorites) {
                await api.post(url);
              } else {
                await api.delete(url);
              }
            } catch (favoriteError: any) {
              const favoriteStatus = getErrorStatus(favoriteError);
              if (favoriteStatus === 409 && finalMyFavorites) {
                finalMyFavorites = true;
              } else {
                throw favoriteError;
              }
            }
          }

          let refreshedCard: Partial<CardType> | null = null;
          try {
            const refreshedResp = await api.get(`/api/adm/cards/${cardId}`);
            const refreshedRaw = refreshedResp?.data?.data || refreshedResp?.data || null;
            if (refreshedRaw) {
              refreshedCard = {
                ...(refreshedRaw as any),
                myFavorites: getServerFavoriteState(refreshedRaw),
              };
            }
          } catch (refreshError) {
          }

          setFormFields(prevFields => prevFields.map(field => ({
            ...field,
            originalValue: field.value,
            isEditing: false
          })));
          setHasChanges(false);

          const updatedCardForState: CardType = {
            ...(editedCard as CardType),
            ...(refreshedCard as any),
            myFavorites: refreshedCard ? getServerFavoriteState(refreshedCard) : finalMyFavorites,
            annotation: refreshedCard ? (refreshedCard as any)?.annotation : (annotation || null),
            card_fields: formFields.map(ff => ({ ...ff, isEditing: false }))
          };

          onSave!(updatedCardForState);
          addToast({ type: 'success', title: 'Card atualizado!' });
          onClose();

        } catch (error: any) {
          throw error;
        }
      }
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Erro ao salvar',
        description: error?.response?.data?.message || 'Tente novamente mais tarde'
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent ref={modalContentRef} onClick={e => e.stopPropagation()}>
        <ModalCloseButton onClick={onClose}>&times;</ModalCloseButton>
        <ModalWrapper>
          <MainContent>
            <ModalHeaderRow>
              <ModalHeaderTitle>{editedCard.description || 'Novo Card'}</ModalHeaderTitle>
              {!isNewCard && (
                <FavoriteButton
                  onClick={handleFavoriteToggle}
                  title={editedCard.myFavorites ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                  aria-label={editedCard.myFavorites ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                  aria-pressed={editedCard.myFavorites || false}
                  $active={editedCard.myFavorites || false}
                >
                  {editedCard.myFavorites ? '❤️' : '🤍'}
                </FavoriteButton>
              )}
            </ModalHeaderRow>

            <ScrollContainer ref={scrollRef}>
              {fieldGroups.requiredAndEmpty.length > 0 && (
                <FieldSectionContainer>
                  <SidebarTitle>Campos Obrigatórios Pendentes ({fieldGroups.requiredAndEmpty.reduce((total, section) => total + section.fields.length, 0)})</SidebarTitle>
                  {fieldGroups.requiredAndEmpty.map(section => (
                    <div key={`required-${section.sectionId ?? 'no-section'}-${section.sectionTitle}`}>
                      <SubSectionTitle>{displaySectionHeading(section.sectionTitle)}</SubSectionTitle>
                      {section.fields.map(field => (
                        <FieldGroupRenderer
                          key={`required-${section.sectionId ?? 'no-section'}-${field.id}`}
                          field={field}
                          isNewCard={isNewCard}
                          onFieldChange={handleFieldChange}
                          onToggleEdit={toggleFieldEdit}
                          hasError={Boolean(fieldErrors[field.id])}
                          errorMessage={fieldErrors[field.id]}
                          onCreateSelectOption={handleCreateCardFieldOption}
                        />
                      ))}
                    </div>
                  ))}
                </FieldSectionContainer>
              )}

              {fieldGroups.filled.length > 0 && (
                <FieldSectionContainer>
                  <SidebarTitle>Campos Preenchidos ({fieldGroups.filled.reduce((total, section) => total + section.fields.length, 0)})</SidebarTitle>
                  {fieldGroups.filled.map(section => (
                    <div key={`filled-${section.sectionId ?? 'no-section'}-${section.sectionTitle}`}>
                      <SubSectionTitle>{displaySectionHeading(section.sectionTitle)}</SubSectionTitle>
                      {section.fields.map(field => (
                        <FieldGroupRenderer
                          key={`filled-${section.sectionId ?? 'no-section'}-${field.id}`}
                          field={field}
                          isNewCard={isNewCard}
                          onFieldChange={handleFieldChange}
                          onToggleEdit={toggleFieldEdit}
                          hasError={Boolean(fieldErrors[field.id])}
                          errorMessage={fieldErrors[field.id]}
                          onCreateSelectOption={handleCreateCardFieldOption}
                        />
                      ))}
                    </div>
                  ))}
                </FieldSectionContainer>
              )}

              {fieldGroups.optionalAndEmpty.length > 0 && (
                <FieldSectionContainer>
                  <SidebarTitle>Campos Opcionais ({fieldGroups.optionalAndEmpty.reduce((total, section) => total + section.fields.length, 0)})</SidebarTitle>
                  {fieldGroups.optionalAndEmpty.map(section => (
                    <div key={`optional-${section.sectionId ?? 'no-section'}-${section.sectionTitle}`}>
                      <SubSectionTitle>{displaySectionHeading(section.sectionTitle)}</SubSectionTitle>
                      {section.fields.map(field => (
                        <FieldGroupRenderer
                          key={`optional-${section.sectionId ?? 'no-section'}-${field.id}`}
                          field={field}
                          isNewCard={isNewCard}
                          onFieldChange={handleFieldChange}
                          onToggleEdit={toggleFieldEdit}
                          hasError={Boolean(fieldErrors[field.id])}
                          errorMessage={fieldErrors[field.id]}
                          onCreateSelectOption={handleCreateCardFieldOption}
                        />
                      ))}
                    </div>
                  ))}
                </FieldSectionContainer>
              )}
            </ScrollContainer>

            {effectiveHasChanges && (
              <ModalFooter>
                <ModalActions>
                  <CancelButton onClick={onClose}>Cancelar</CancelButton>
                  <SubmitButton onClick={handleSave} disabled={isSaving}>
                    {isSaving ? 'Salvando...' : 'Salvar'}
                  </SubmitButton>
                </ModalActions>
              </ModalFooter>
            )}
          </MainContent>

          <Sidebar>
            <SidebarSection>
              <SidebarTitle>Título</SidebarTitle>
              <FieldInput
                value={editedCard.description || ''}
                onChange={e => handleDescriptionChange(e.target.value)}
                placeholder="Digite o título..."
              />
            </SidebarSection>

            <SidebarSection>
              <SidebarTitle>Prioridade</SidebarTitle>
              <FieldSelect
                value={editedCard.priority || 'important'}
                onChange={e => handlePriorityChange(e.target.value as CardType['priority'])}
              >
                <option value="urgent">Urgente</option>
                <option value="important">Importante</option>
                <option value="not important">Não importante</option>
              </FieldSelect>
            </SidebarSection>

            <SidebarSection>
              <SidebarTitle>Datas</SidebarTitle>
              <DatesContainer>
                <DateItem>
                  <span>Data de criação:</span>
                  <DateValue>{formatDateAndTime((editedCard as any)?.created_at || (editedCard as any)?.createdAt || (card as any)?.created_at || (card as any)?.createdAt)}</DateValue>
                </DateItem>
                <DateItem>
                  <span>Data de atualização:</span>
                  <DateValue>{formatDateAndTime((editedCard as any)?.updated_at || (editedCard as any)?.updatedAt || (card as any)?.updated_at || (card as any)?.updatedAt)}</DateValue>
                </DateItem>
              </DatesContainer>
            </SidebarSection>

            <SidebarSection>
              <SidebarTitle>Anotações</SidebarTitle>
              <NotesTextarea
                value={annotation}
                onChange={handleAnnotationChange}
                placeholder="Adicionar anotação..."
                rows={4}
              />
            </SidebarSection>
          </Sidebar>
        </ModalWrapper>
      </ModalContent>
    </ModalOverlay>
  );
}

export default CardModal;